import * as React from "react";
import useSWR from "swr";
import socketIOClient from "socket.io-client";
import fetcher from "./fetcher";

const buildSalesKey = (route) =>
  route ? `/api/autographPurchase?route=${route}` : null;

const parseSalesResponse = (data, prevSales) => {
  if (!data) {
    return { sales: prevSales, queueSystems: [] };
  }

  const salesMap = {};
  const queueSystemsData = [];

  Object.keys(data).forEach((queueSystemId) => {
    const arrayMethod = queueSystemId === "main" ? "unshift" : "push";
    queueSystemsData[arrayMethod]({
      queue_system_id: queueSystemId,
      label: data[queueSystemId].label,
      unfulfilled: 0,
    });
    for (const sale of data[queueSystemId].sales) {
      salesMap[sale.autograph_purchase_id] = sale;
    }
  });

  // Preserve cancelled/archived orders from previous state
  for (const sale of prevSales) {
    if (!salesMap[sale.autograph_purchase_id]) {
      salesMap[sale.autograph_purchase_id] = { ...sale, cancelled: true };
    }
  }

  const sales = Object.values(salesMap).sort(
    (a, b) => a.queue_number - b.queue_number,
  );

  return { sales, queueSystems: queueSystemsData };
};

const computeSalesCountMap = (sales) => {
  const countMap = {};
  const queueSystemsUnfulfilled = {};

  for (const sale of sales) {
    if (sale.cancelled || sale.archived) continue;

    if (!countMap[sale.product_id]) {
      countMap[sale.product_id] = { signed: 0, unsigned: 0 };
    }

    const queueSystemId = sale.queue_system_id || "main";
    if (!queueSystemsUnfulfilled[queueSystemId]) {
      queueSystemsUnfulfilled[queueSystemId] = 0;
    }

    if (!sale.signed) {
      countMap[sale.product_id].unsigned++;
      queueSystemsUnfulfilled[queueSystemId]++;
    } else {
      countMap[sale.product_id].signed++;
    }
  }

  return { countMap, queueSystemsUnfulfilled };
};

const sortQueueSystems = (queueSystems, unfulfilledData) =>
  queueSystems
    .map((qs) => ({
      ...qs,
      unfulfilled: unfulfilledData[qs.queue_system_id] ?? 0,
    }))
    .toSorted((a, b) =>
      b.queue_system_id === "main"
        ? 1
        : a.queue_system_id === "main"
        ? -1
        : (b.unfulfilled > 0 && a.unfulfilled > 0) ||
          (b.unfulfilled <= 0 && a.unfulfilled <= 0)
        ? a.queue_system_id - b.queue_system_id
        : b.unfulfilled > 0
        ? 1
        : -1,
    );

export default function useSales({ route, influencerId }) {
  const prevSalesRef = React.useRef([]);
  const influencerIdRef = React.useRef(influencerId);

  React.useEffect(() => {
    influencerIdRef.current = influencerId;
  }, [influencerId]);

  const key = buildSalesKey(route);

  const {
    data: rawData,
    error,
    mutate: refresh,
  } = useSWR(key, fetcher, { refreshInterval: 10000 });

  const { sales, queueSystems: rawQueueSystems } = React.useMemo(() => {
    const result = parseSalesResponse(rawData, prevSalesRef.current);
    prevSalesRef.current = result.sales;
    return result;
  }, [rawData]);

  const { countMap: salesCountMap, queueSystemsUnfulfilled } = React.useMemo(
    () => computeSalesCountMap(sales),
    [sales],
  );

  const queueSystems = React.useMemo(
    () => sortQueueSystems(rawQueueSystems, queueSystemsUnfulfilled),
    [rawQueueSystems, queueSystemsUnfulfilled],
  );

  // Socket.IO listener for real-time updates
  React.useEffect(() => {
    const socket = socketIOClient({ transports: ["websocket"] });

    socket.on("updateAutographPurchase", (data) => {
      if (
        data.influencer_id &&
        data.influencer_id !== influencerIdRef.current
      ) {
        return;
      }

      refresh(
        (currentData) => {
          if (!currentData) return currentData;

          const updated = {};
          Object.keys(currentData).forEach((queueSystemId) => {
            updated[queueSystemId] = {
              ...currentData[queueSystemId],
              sales: currentData[queueSystemId].sales.map((sale) =>
                sale.autograph_purchase_id === data.autograph_purchase_id
                  ? { ...sale, ...data.updated }
                  : sale,
              ),
            };
          });
          return updated;
        },
        { revalidate: false },
      );
    });

    return () => {
      socket.disconnect();
    };
  }, [refresh]);

  return {
    sales,
    queueSystems,
    salesCountMap,
    isLoading: !rawData && !error,
    refresh,
  };
}
